import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { watchlistAPI, tmdbAPI } from '../services/api';
import {
  HiOutlineArrowLeft,
  HiCheck,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlinePlay,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w300';
const TMDB_STILL_BASE = 'https://image.tmdb.org/t/p/w200';

function MobileSeriesDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [tvDetails, setTvDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSeasons, setExpandedSeasons] = useState({});
  const [loadingSeasons, setLoadingSeasons] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const watchlistRes = await watchlistAPI.getItem(id);
      setItem(watchlistRes.data);

      const tvRes = await tmdbAPI.getTVDetails(watchlistRes.data.tmdb_id);
      setTvDetails(tvRes.data);

      // Initialize seasons if not already set
      if (!watchlistRes.data.seasons || watchlistRes.data.seasons.length === 0) {
        const seasons = tvRes.data.seasons
          ?.filter((s) => s.season_number > 0)
          .map((s) => ({
            season_number: s.season_number,
            watched: false,
            episodes: [],
          }));
        if (seasons && seasons.length > 0) {
          await watchlistAPI.update(id, { seasons });
          setItem((prev) => ({ ...prev, seasons }));
        }
      }
    } catch (error) {
      console.error('Error loading series:', error);
      toast.error(t('seriesDetail.errorLoadingSeries'));
      navigate('/series');
    } finally {
      setLoading(false);
    }
  };

  const loadSeasonEpisodes = async (seasonNumber) => {
    if (loadingSeasons[seasonNumber]) return;

    setLoadingSeasons((prev) => ({ ...prev, [seasonNumber]: true }));
    try {
      const response = await tmdbAPI.getSeasonDetails(item.tmdb_id, seasonNumber);
      const episodes = response.data.episodes?.map((ep) => ({
        episode_number: ep.episode_number,
        name: ep.name || '',
        still_path: ep.still_path || null,
        watched: false,
      })) || [];

      // Check if we already have episode data
      const existingSeason = item.seasons?.find((s) => s.season_number === seasonNumber);
      if (existingSeason?.episodes?.length > 0) {
        // Merge existing watched status
        episodes.forEach((ep) => {
          const existing = existingSeason.episodes.find(
            (e) => e.episode_number === ep.episode_number
          );
          if (existing) {
            ep.watched = existing.watched;
          }
        });
      }

      // Update seasons
      const updatedSeasons = item.seasons?.map((s) =>
        s.season_number === seasonNumber ? { ...s, episodes } : s
      ) || [];

      await watchlistAPI.update(id, { seasons: updatedSeasons });
      setItem((prev) => ({ ...prev, seasons: updatedSeasons }));
    } catch (error) {
      console.error('Error loading season:', error);
      toast.error(t('seriesDetail.errorLoadingSeason'));
    } finally {
      setLoadingSeasons((prev) => ({ ...prev, [seasonNumber]: false }));
    }
  };

  const toggleSeason = async (seasonNumber) => {
    const isExpanded = expandedSeasons[seasonNumber];

    if (!isExpanded) {
      // Load episodes if not loaded
      const season = item.seasons?.find((s) => s.season_number === seasonNumber);
      if (!season?.episodes?.length) {
        await loadSeasonEpisodes(seasonNumber);
      }
    }

    setExpandedSeasons((prev) => ({
      ...prev,
      [seasonNumber]: !prev[seasonNumber],
    }));
  };

  const toggleSeasonWatched = async (seasonNumber) => {
    const season = item.seasons?.find((s) => s.season_number === seasonNumber);
    if (!season) return;

    const newWatched = !season.watched;
    const updatedSeasons = item.seasons?.map((s) =>
      s.season_number === seasonNumber
        ? {
            ...s,
            watched: newWatched,
            episodes: s.episodes?.map((ep) => ({ ...ep, watched: newWatched })) || [],
          }
        : s
    );

    try {
      await watchlistAPI.update(id, { seasons: updatedSeasons });
      setItem((prev) => ({ ...prev, seasons: updatedSeasons }));
      toast.success(newWatched ? t('seriesDetail.seasonMarkedAsWatched') : t('seriesDetail.seasonUnmarked'));
    } catch (error) {
      toast.error(t('seriesDetail.errorUpdating'));
    }
  };

  const toggleEpisodeWatched = async (seasonNumber, episodeNumber) => {
    const season = item.seasons?.find((s) => s.season_number === seasonNumber);
    if (!season) return;

    const episode = season.episodes?.find((e) => e.episode_number === episodeNumber);
    if (!episode) return;

    const newWatched = !episode.watched;
    const updatedEpisodes = season.episodes?.map((ep) =>
      ep.episode_number === episodeNumber ? { ...ep, watched: newWatched } : ep
    );

    // Check if all episodes are watched
    const allWatched = updatedEpisodes?.every((ep) => ep.watched);

    const updatedSeasons = item.seasons?.map((s) =>
      s.season_number === seasonNumber
        ? { ...s, watched: allWatched, episodes: updatedEpisodes }
        : s
    );

    try {
      await watchlistAPI.update(id, { seasons: updatedSeasons });
      setItem((prev) => ({ ...prev, seasons: updatedSeasons }));
    } catch (error) {
      toast.error(t('seriesDetail.errorUpdating'));
    }
  };

  const getSeasonProgress = (season) => {
    if (season.watched) return 100;
    if (!season.episodes?.length) return 0;
    const watched = season.episodes.filter((ep) => ep.watched).length;
    return Math.round((watched / season.episodes.length) * 100);
  };

  const getTotalProgress = () => {
    if (!item?.seasons?.length) return 0;
    let total = 0;
    let watched = 0;
    item.seasons.forEach((s) => {
      if (s.watched) {
        total += s.episodes?.length || 1;
        watched += s.episodes?.length || 1;
      } else if (s.episodes?.length) {
        total += s.episodes.length;
        watched += s.episodes.filter((ep) => ep.watched).length;
      }
    });
    return total > 0 ? Math.round((watched / total) * 100) : 0;
  };

  const getWatchedEpisodesCount = () => {
    if (!item?.seasons?.length) return 0;
    let watched = 0;
    let total = 0;
    item.seasons.forEach((s) => {
      if (s.episodes?.length) {
        total += s.episodes.length;
        watched += s.episodes.filter((ep) => ep.watched).length;
      }
    });
    return { watched, total };
  };

  if (loading) {
    return (
      <div className="mobile-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const { watched: watchedCount, total: totalEpisodes } = getWatchedEpisodesCount();

  return (
    <div className="mobile-series-detail">
      {/* Header */}
      <div className="mobile-series-detail__header">
        <button
          className="mobile-series-detail__back"
          onClick={() => navigate('/series')}
        >
          <HiOutlineArrowLeft size={24} />
        </button>
        <h1 className="mobile-series-detail__title">{item?.title}</h1>
      </div>

      {/* Poster & Info */}
      <div className="mobile-series-detail__info">
        <div className="mobile-series-detail__poster">
          {item?.poster_path ? (
            <img
              src={`${TMDB_IMAGE_BASE}${item.poster_path}`}
              alt={item.title}
            />
          ) : (
            <div className="mobile-series-detail__poster-placeholder">
              {t('media.noImage')}
            </div>
          )}
        </div>

        <div className="mobile-series-detail__meta">
          <p className="mobile-series-detail__stats">
            {tvDetails?.number_of_seasons} {t('series.seasons').toLowerCase()} •{' '}
            {tvDetails?.number_of_episodes} {t('series.episodes').toLowerCase()}
          </p>
          
          {/* Progress */}
          <div className="mobile-series-detail__progress">
            <div className="mobile-series-detail__progress-header">
              <span>{t('seriesDetail.totalProgress')}</span>
              <span>{getTotalProgress()}%</span>
            </div>
            <div className="mobile-series-detail__progress-bar">
              <div
                className="mobile-series-detail__progress-fill"
                style={{ width: `${getTotalProgress()}%` }}
              />
            </div>
            <p className="mobile-series-detail__progress-count">
              {watchedCount} / {totalEpisodes} {t('episodes.watched').toLowerCase()}
            </p>
          </div>
        </div>
      </div>

      {/* Overview */}
      {tvDetails?.overview && (
        <div className="mobile-series-detail__overview">
          <p>{tvDetails.overview}</p>
        </div>
      )}

      {/* Seasons */}
      <div className="mobile-series-detail__seasons">
        <h2 className="mobile-series-detail__section-title">{t('series.seasons')}</h2>
        
        {item?.seasons?.map((season) => {
          const progress = getSeasonProgress(season);
          const isExpanded = expandedSeasons[season.season_number];
          const isLoading = loadingSeasons[season.season_number];

          return (
            <div key={season.season_number} className="mobile-season">
              <div
                className="mobile-season__header"
                onClick={() => toggleSeason(season.season_number)}
              >
                <div className="mobile-season__info">
                  <button
                    className={`mobile-season__check ${season.watched ? 'watched' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSeasonWatched(season.season_number);
                    }}
                  >
                    <HiCheck size={18} />
                  </button>
                  <div>
                    <h4 className="mobile-season__title">
                      {t('series.season')} {season.season_number}
                    </h4>
                    <span className="mobile-season__progress-text">
                      {progress}% {t('episodes.watched').toLowerCase()}
                    </span>
                  </div>
                </div>
                <div className="mobile-season__actions">
                  <div className="mobile-season__progress-bar">
                    <div
                      className="mobile-season__progress-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  {isExpanded ? (
                    <HiOutlineChevronUp size={20} />
                  ) : (
                    <HiOutlineChevronDown size={20} />
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="mobile-season__episodes">
                  {isLoading ? (
                    <div className="mobile-season__loading">
                      <div className="spinner" style={{ width: '24px', height: '24px' }} />
                    </div>
                  ) : season.episodes?.length > 0 ? (
                    <div className="mobile-episodes-list">
                      {season.episodes.map((ep) => (
                        <div
                          key={ep.episode_number}
                          className={`mobile-episode ${ep.watched ? 'watched' : ''}`}
                          onClick={() =>
                            toggleEpisodeWatched(season.season_number, ep.episode_number)
                          }
                        >
                          <div className="mobile-episode__still">
                            {ep.still_path ? (
                              <img
                                src={`${TMDB_STILL_BASE}${ep.still_path}`}
                                alt={ep.name}
                              />
                            ) : (
                              <div className="mobile-episode__still-placeholder">
                                <HiOutlinePlay size={20} />
                              </div>
                            )}
                            {ep.watched && (
                              <div className="mobile-episode__watched-badge">
                                <HiCheck size={12} />
                              </div>
                            )}
                          </div>
                          <div className="mobile-episode__info">
                            <span className="mobile-episode__number">
                              EP{ep.episode_number}
                            </span>
                            <span className="mobile-episode__name">
                              {ep.name || `${t('series.episode')} ${ep.episode_number}`}
                            </span>
                          </div>
                          <div className={`mobile-episode__check ${ep.watched ? 'watched' : ''}`}>
                            <HiCheck size={16} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mobile-season__empty">
                      {t('seriesDetail.noEpisodesAvailable')}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom spacer */}
      <div className="mobile-series-detail__spacer" />
    </div>
  );
}

export default MobileSeriesDetail;
